import React, { useState, useEffect } from 'react';
import { Users, Activity, Truck, AlertTriangle } from 'lucide-react';
import StatCard from '../../shared/StatCard';
import { api } from '../../../utils/api';

function AlertBadge({ alert }) {
  if (!alert) return null;
  var severity = alert.severity;
  var msg = alert.title || '';

  if (severity === 'CRITICAL') {
    return React.createElement('span', {
      className: 'px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full text-xs font-medium animate-pulse',
      'data-testid': 'alert-badge-critical'
    }, msg.length > 30 ? msg.substring(0, 30) + '...' : msg);
  }
  if (severity === 'WARNING') {
    return React.createElement('span', {
      className: 'px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full text-xs font-medium',
      'data-testid': 'alert-badge-warning'
    }, msg.length > 30 ? msg.substring(0, 30) + '...' : msg);
  }
  return null;
}

function StatusBadge({ status, alert }) {
  if (alert) {
    return React.createElement(AlertBadge, { alert: alert });
  }
  return React.createElement('span', {
    className: 'px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm'
  }, status || 'Active');
}

function DriverRow({ driver, alert }) {
  var name = (driver.users?.first_name || '') + ' ' + (driver.users?.last_name || '');
  var initial = driver.users?.first_name?.[0] || 'D';

  return React.createElement('tr', {
    key: driver.driver_id,
    className: 'border-t border-slate-700'
  }, [
    React.createElement('td', { key: 'name', className: 'py-4' },
      React.createElement('div', { className: 'flex items-center gap-3' }, [
        React.createElement('div', {
          key: 'avatar',
          className: 'w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center'
        }, React.createElement('span', { className: 'text-sm text-white' }, initial)),
        React.createElement('span', { key: 'label', className: 'text-white' }, name)
      ])
    ),
    React.createElement('td', { key: 'status' },
      React.createElement(StatusBadge, { status: driver.status, alert: alert })
    ),
    React.createElement('td', { key: 'type', className: 'text-slate-400' }, 'FRHV'),
    React.createElement('td', { key: 'active', className: 'text-slate-400' }, '-')
  ]);
}

function MobileDriverCard({ driver, alert }) {
  var name = (driver.users?.first_name || '') + ' ' + (driver.users?.last_name || '');
  var initial = driver.users?.first_name?.[0] || 'D';

  return (
    <div key={driver.driver_id} className="py-3">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
          <span className="text-xs text-white">{initial}</span>
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-white text-sm block truncate">{name}</span>
          <span className="text-slate-400 text-xs">FRHV</span>
        </div>
        <StatusBadge status={driver.status} alert={alert} />
      </div>
    </div>
  );
}

const OverviewTab = ({ stats, drivers }) => {
  var [alerts, setAlerts] = useState({});

  useEffect(function () {
    api('/driver-alerts').then(function (data) {
      setAlerts(data?.alerts || {});
    }).catch(function () {});
    // Refresh alerts every 60s
    var interval = setInterval(function () {
      api('/driver-alerts').then(function (data) {
        setAlerts(data?.alerts || {});
      }).catch(function () {});
    }, 60000);
    return function () { clearInterval(interval); };
  }, []);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard title="Total Drivers" value={stats?.total_drivers || 0} icon={Users} color="emerald" />
        <StatCard title="Active Today" value={stats?.active_drivers_today || 0} icon={Activity} color="blue" />
        <StatCard title="Vehicles" value={stats?.total_vehicles || 0} icon={Truck} color="purple" />
        <StatCard title="Non-Conformances (7d)" value={stats?.crossings_this_week || 0} icon={AlertTriangle} color="amber" />
      </div>

      <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-4 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-white mb-4">Fleet Overview</h2>

        <div className="block sm:hidden divide-y divide-slate-700">
          {drivers.length === 0 ? (
            <div className="py-6 text-center text-slate-400 text-sm">
              No drivers yet. Add your first driver to get started.
            </div>
          ) : (
            drivers.slice(0, 5).map(function (driver) {
              return React.createElement(MobileDriverCard, {
                key: driver.driver_id,
                driver: driver,
                alert: alerts[driver.driver_id]
              });
            })
          )}
        </div>

        <div className="hidden sm:block overflow-x-auto">
          {React.createElement('table', { className: 'w-full' }, [
            React.createElement('thead', { key: 'thead' },
              React.createElement('tr', { className: 'text-left text-slate-400 text-sm' }, [
                React.createElement('th', { key: 'h1', className: 'pb-4' }, 'Driver'),
                React.createElement('th', { key: 'h2', className: 'pb-4' }, 'Status'),
                React.createElement('th', { key: 'h3', className: 'pb-4' }, 'Diary Type'),
                React.createElement('th', { key: 'h4', className: 'pb-4' }, 'Last Active')
              ])
            ),
            React.createElement('tbody', { key: 'tbody', className: 'text-white' },
              drivers.length === 0
                ? React.createElement('tr', { key: 'empty' },
                    React.createElement('td', {
                      colSpan: 4,
                      className: 'py-8 text-center text-slate-400'
                    }, 'No drivers yet. Add your first driver to get started.')
                  )
                : drivers.slice(0, 5).map(function (driver) {
                    return React.createElement(DriverRow, {
                      key: driver.driver_id,
                      driver: driver,
                      alert: alerts[driver.driver_id]
                    });
                  })
            )
          ])}
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
